import React, { useState, useEffect, useCallback } from 'react';
import { 
  PoundSterling, 
  CheckCircle, 
  Clock, 
  Loader2, 
  Download, 
  Check, 
  Wallet,
  Filter
} from 'lucide-react';
import type { Team, Commission, CommissionStatus, TeamMember } from '../../types';
import { getCommissions, updateCommissionStatus, bulkUpdateCommissionStatus, exportCommissionsToCSV } from '../../services/commissions';
import { getTeamMembers } from '../../services/teams';
import { formatCurrency } from '../../services/billing';

interface AdminCommissionsPageProps {
  team: Team;
  userId: string;
}

const AdminCommissionsPage: React.FC<AdminCommissionsPageProps> = ({ team, userId }) => {
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [statusFilter, setStatusFilter] = useState<CommissionStatus | ''>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [updating, setUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [commissionsData, membersData] = await Promise.all([
      getCommissions(team.id),
      getTeamMembers(team.id),
    ]);
    setCommissions(commissionsData);
    setMembers(membersData);
    setLoading(false);
  }, [team.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredCommissions = statusFilter
    ? commissions.filter((c) => c.status === statusFilter)
    : commissions;

  const getMemberEmail = (userId: string) => {
    const member = members.find((m) => m.user_id === userId);
    return member?.email || userId.slice(0, 8);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredCommissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCommissions.map((c) => c.id)));
    }
  };

  const handleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleBulkAction = async (status: CommissionStatus) => {
    if (selectedIds.size === 0) return;
    setUpdating(true);
    const success = await bulkUpdateCommissionStatus(
      Array.from(selectedIds),
      status,
      userId
    );
    setUpdating(false);
    if (success) {
      setSelectedIds(new Set());
      fetchData();
    }
  };

  const handleSingleAction = async (commissionId: string, status: CommissionStatus) => {
    setUpdating(true);
    const success = await updateCommissionStatus(commissionId, status, userId);
    setUpdating(false);
    if (success) {
      fetchData();
    }
  };

  const handleExport = () => {
    const csv = exportCommissionsToCSV(filteredCommissions);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `commissions-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 rounded-full">
            <Clock size={10} />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full">
            <CheckCircle size={10} />
            Approved
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full">
            <CheckCircle size={10} />
            Paid
          </span>
        );
      default:
        return null;
    }
  };

  const totals = {
    pending: commissions.filter((c) => c.status === 'pending').reduce((sum, c) => sum + c.commission_amount_cents, 0),
    approved: commissions.filter((c) => c.status === 'approved').reduce((sum, c) => sum + c.commission_amount_cents, 0),
    paid: commissions.filter((c) => c.status === 'paid').reduce((sum, c) => sum + c.commission_amount_cents, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Commission Management</h1>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => setStatusFilter(statusFilter === 'pending' ? '' : 'pending')}
          className={`p-5 border rounded-xl text-left transition-colors ${
            statusFilter === 'pending'
              ? 'bg-amber-500/20 border-amber-500'
              : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-amber-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Pending Approval</span>
          </div>
          <div className="text-2xl font-bold text-amber-400">
            {formatCurrency(totals.pending, 'gbp')}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {commissions.filter((c) => c.status === 'pending').length} commissions
          </div>
        </button>

        <button
          onClick={() => setStatusFilter(statusFilter === 'approved' ? '' : 'approved')}
          className={`p-5 border rounded-xl text-left transition-colors ${
            statusFilter === 'approved'
              ? 'bg-blue-500/20 border-blue-500'
              : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-blue-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Approved (Unpaid)</span>
          </div>
          <div className="text-2xl font-bold text-blue-400">
            {formatCurrency(totals.approved, 'gbp')}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {commissions.filter((c) => c.status === 'approved').length} commissions
          </div>
        </button>

        <button
          onClick={() => setStatusFilter(statusFilter === 'paid' ? '' : 'paid')}
          className={`p-5 border rounded-xl text-left transition-colors ${
            statusFilter === 'paid'
              ? 'bg-emerald-500/20 border-emerald-500'
              : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-emerald-500/20 rounded-lg">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Paid Out</span>
          </div>
          <div className="text-2xl font-bold text-emerald-400">
            {formatCurrency(totals.paid, 'gbp')}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {commissions.filter((c) => c.status === 'paid').length} commissions
          </div>
        </button>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
          <span className="text-sm text-orange-400 font-medium">
            {selectedIds.size} selected
          </span>
          <div className="flex-1" />
          <button
            onClick={() => handleBulkAction('approved')}
            disabled={updating}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 text-white rounded-lg transition-colors"
          >
            {updating ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Approve
          </button>
          <button
            onClick={() => handleBulkAction('paid')}
            disabled={updating}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white rounded-lg transition-colors"
          >
            {updating ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />}
            Mark Paid
          </button>
        </div>
      )}

      {/* Commission Table */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            All Commissions
            {statusFilter && (
              <span className="ml-2 text-sm font-normal text-slate-400">
                ({statusFilter})
              </span>
            )}
          </h2>
          {statusFilter && (
            <button
              onClick={() => setStatusFilter('')}
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
            >
              <Filter size={14} />
              Clear filter
            </button>
          )}
        </div>

        {filteredCommissions.length === 0 ? (
          <div className="p-12 text-center">
            <PoundSterling className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No commissions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-900/50">
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredCommissions.length && filteredCommissions.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Salesperson
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {filteredCommissions.map((commission) => {
                  const contact = commission.contact as { first_name?: string; last_name?: string; company?: string; email?: string } | null;
                  const customerName = contact
                    ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company || contact.email || 'Unknown'
                    : 'Unknown Customer';

                  return (
                    <tr key={commission.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(commission.id)}
                          onChange={() => handleSelect(commission.id)}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">
                          {getMemberEmail(commission.sales_user_id)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">{customerName}</div>
                        {contact?.company && customerName !== contact.company && (
                          <div className="text-xs text-slate-400">{contact.company}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-300">
                          {new Date(commission.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-sm text-white">
                          {formatCurrency(commission.revenue_amount_cents, commission.currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-sm font-medium text-emerald-400">
                          {formatCurrency(commission.commission_amount_cents, commission.currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getStatusBadge(commission.status)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {commission.status === 'pending' && (
                            <button
                              onClick={() => handleSingleAction(commission.id, 'approved')}
                              disabled={updating}
                              className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded transition-colors"
                              title="Approve"
                            >
                              <Check size={16} />
                            </button>
                          )}
                          {commission.status === 'approved' && (
                            <button
                              onClick={() => handleSingleAction(commission.id, 'paid')}
                              disabled={updating}
                              className="p-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded transition-colors"
                              title="Mark Paid"
                            >
                              <Wallet size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCommissionsPage;
